import Button from '@/components/Button'
import Card from '@/components/Card'
import DateDisplay from '@/components/DateDisplay'
import Layout, { Stack } from '@/components/Layout'
import LivePulse from '@/components/LivePulse'
import StatusBadge from '@/components/StatusBadge'
import Text from '@/components/Text'
import { useBooking } from '@/context/booking'
import { useBookmarkItems } from '@/context/bookmarkedItems'
import { useFeatureToggle } from '@/context/featureToggles'
import { CalendarUrl, createCalendarUrl } from '@/lib/calendarUrl'
import { getTimeFromNow } from '@/lib/datetime'
import { GeneratingPage, eventsTracker } from '@/lib/eventsTracker'
import { lobbyUrl } from '@/lib/lobbyUrl'
import { makeBooking } from '@/lib/makeBooking'
import { JoinBookedSessionCardAction } from '@/shared/api-generated-types'
import AddToCalendar from '@/widgets/AddToCalendar'
import BookingSuccess from '@/widgets/BookingSuccess'
import Modal from '@/widgets/Modal'
import { useTranslation } from 'next-i18next'
import router from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { CardPrimaryAction, GuideByline, VariantProps } from '../index'
import { formatLiveTime as formatLiveTimeStyles } from '../style'
import * as styles from './styles'

const Feed = ({
  badgesTheme,
  isLive,
  imageUrl,
  accessDisplay,
  guideAvatarUrl,
  guideByline,
  navigationAction,
  navigateToGuideAction,
  primaryAction,
  title,
  id,
  start,
  end,
  fluid = false,
  wide = false,
  fixedAspectRatio = true,
  isSchedule = false,
  searchResultIndex, // 70e7b1d8-0cb9-4883-8927-1a69372cfac2
  generatingPage,
  isCommunityLedClass = false,
  isEncore = false,
  classIsNew = false,
  sessionId,
  sponsor,
  classId,
  livenessType,
  bookmarked,
}: Partial<VariantProps>): JSX.Element => {
  const showAltLiveLabels = useFeatureToggle('showAltLiveLabels')
  const { t } = useTranslation('classes')
  const { state: bookingState, dispatch: bookingDispatch } = useBooking()
  const [buttonLabel, setButtonLabel] = useState<string | undefined>(primaryAction?.label && t('slotCard.book'))
  const [isBooking, setIsBooking] = useState<boolean>(false)
  const { state: bookmarkState } = useBookmarkItems()
  const [isBooked, setIsBooked] = useState<boolean>(false)
  const [bookingFailed, setBookingFailed] = useState<boolean>(false)
  const [bookingSuccess, setBookingSuccess] = useState<boolean>(false)
  const [openAddtoCalendar, setOpenAddtoCalendar] = useState<boolean>(false)
  const [calendarUrl, setCalendarUrl] = useState<CalendarUrl>({
    googleCalendarUrl: '',
    yahooCalendarUrl: '',
    outlookCalendarUrl: '',
    iCalUrl: '',
  })
  const [isBookmarked, setIsBookmarked] = useState<boolean | undefined>(bookmarked)

  const [isLobbyButton, setIsLobbyButton] = useState<boolean>(
    primaryAction?.__typename === 'JoinBookedSessionCardAction' ||
      primaryAction?.__typename === 'JoinLiveSessionCardAction',
  )

  const bookedPrimaryAction = {
    __typename: 'JoinBookedSessionCardAction',
    url: lobbyUrl(`/session/${sessionId}`),
    label: t('slotCard.enter'),
  } as JoinBookedSessionCardAction

  const handleBookingAction = (): void => {
    bookingDispatch({ type: 'FETCHING', payload: { sessionId } })
    makeBooking({ classId: classId ?? undefined, sessionId }).then((response) => {
      if (response) {
        bookingDispatch({ type: 'BOOKED', payload: { sessionId } })
        setCalendarUrl(createCalendarUrl({ start, end, title, sessionId }))
        toggleBookingSuccess()
      } else bookingDispatch({ type: 'FAILED', payload: { sessionId } })
    })
  }

  const toggleBookingSuccess = (): void => {
    setBookingSuccess(!bookingSuccess)
  }

  // TODO: Revisit the state mgmt as mentioned here: https://github.com/getsetup-io/gsu-web/pull/65#discussion_r758047383
  useEffect(() => {
    if (bookingState.fetching?.find((s) => s === sessionId)) {
      setButtonLabel(t('slotCard.booking'))
      setIsBooking(true)
    } else if (bookingState.booked?.find((s) => s === sessionId)) {
      setButtonLabel(t('slotCard.booked'))
      setIsBooked(true)
      setIsBooking(false)
    } else if (bookingState.failed?.find((s) => s === sessionId)) {
      setButtonLabel(t('slotCard.bookingFailed'))
      setBookingFailed(true)
      setIsBooking(false)
    }
  }, [bookingState])

  // After 1.5 seconds, lets show the next action for a user to take
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isBooked) {
        setIsBooked(false)
        setButtonLabel(t('slotCard.enter'))
        setIsLobbyButton(true)
      }

      if (bookingFailed) {
        setBookingFailed(false)
        setButtonLabel(t('slotCard.book'))
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [isBooked, bookingFailed])

  useEffect(() => {
    if (classId && bookmarkState.classes?.[classId] === true) {
      setIsBookmarked(true)
    } else if (classId && bookmarkState.classes?.[classId] === false) {
      setIsBookmarked(false)
    }
  }, [bookmarkState, classId])

  const primaryActionButton = useMemo(() => {
    return primaryAction?.label ? (
      <div className={styles.wideButton}>
        <CardPrimaryAction makeBooking={handleBookingAction} primaryAction={primaryAction}>
          <Button size="sm" theme={isLive || isLobbyButton ? 'secondary' : 'primary'} label={buttonLabel} />
        </CardPrimaryAction>
      </div>
    ) : null
  }, [isLive, primaryAction, navigationAction, buttonLabel, isLobbyButton])

  const dateDisplay = useMemo(() => {
    if (!start || !navigationAction) return null
    return (
      <div onClick={() => router.push(navigationAction?.url)}>
        <DateDisplay abbreviated multiline={wide} value={start} />
      </div>
    )
  }, [wide, start])

  const liveTimeStart = useMemo(() => {
    return getTimeFromNow(start)
  }, [start])

  const iconUrl: string | undefined =
    accessDisplay?.__typename === 'LockedAccessDisplay' ? accessDisplay?.iconUrl : undefined

  const accessOverlay: boolean | undefined = accessDisplay?.__typename === 'LockedAccessDisplay' ? true : undefined

  return (
    <>
      {bookingSuccess ? (
        <Modal onDismiss={toggleBookingSuccess}>
          {openAddtoCalendar ? (
            <AddToCalendar
              onDismiss={() => setOpenAddtoCalendar(false)}
              googleCalendarUrl={calendarUrl.googleCalendarUrl}
              yahooCalendarUrl={calendarUrl.yahooCalendarUrl}
              outlookCalendarUrl={calendarUrl.outlookCalendarUrl}
              iCalUrl={calendarUrl.iCalUrl}
            />
          ) : (
            <BookingSuccess onClickAddToCalendar={() => setOpenAddtoCalendar(true)} />
          )}
        </Modal>
      ) : null}
      <Card
        theme="standard"
        overlay={false}
        overflow={false}
        shadow
        backgroundImage={imageUrl}
        accessOverlay={accessOverlay}
        iconImage={iconUrl}
        href={navigationAction?.url}
        fixedAspectRatio={!wide && fixedAspectRatio}
        fluid={fluid}
        wide={wide}
        showFallbackImage
        isSchedule={isSchedule}
        isCommunityLedClass={isCommunityLedClass}
        isEncore={isEncore}
        classIsNew={classIsNew}
        sponsorLogo={sponsor?.logoUrl}
        isBookmarked={isBookmarked}
        classId={classId}
        onClick={() => {
          // 70e7b1d8-0cb9-4883-8927-1a69372cfac2
          if (generatingPage === GeneratingPage.SEARCH)
            eventsTracker.handleSearchOnClick(searchResultIndex as number, generatingPage)
        }}
        header={
          <Layout className="h-100">
            {wide ? (
              <Stack className={wide ? styles.wide : ''} x justifyContent="space-between">
                <div>
                  {navigationAction && (
                    <div onClick={() => router.push(navigationAction?.url)}>
                      <Text
                        title={typeof title === 'string' ? title : ''}
                        tag="h2"
                        weight="lg"
                        size="xs"
                        responsiveSize={{
                          sm: 'sm',
                        }}
                        className={`ellipsis-2L mb-xxs fc-dark`}
                      >
                        {title}
                      </Text>
                    </div>
                  )}
                  <Stack x alignItems={'flex-start'} justifyContent="flex-start">
                    <GuideByline
                      {...{
                        badgesTheme,
                        guideAvatarUrl,
                        guideByline,
                        navigateToGuideAction,
                        isCommunityLedClass,
                        isEncore,
                        livenessType,
                      }}
                    />
                  </Stack>
                </div>
                {isLobbyButton ? (
                  <Stack x={!wide} y={!!wide} alignItems="flex-end" alignSelf="flex-end">
                    <CardPrimaryAction primaryAction={bookedPrimaryAction}>
                      <Button size="sm" theme="secondary" label={bookedPrimaryAction.label} />
                    </CardPrimaryAction>
                  </Stack>
                ) : (
                  primaryActionButton
                )}
              </Stack>
            ) : (
              <Stack y justifyContent="center">
                {navigationAction && (
                  <div onClick={() => router.push(navigationAction?.url)}>
                    <Text
                      title={typeof title === 'string' ? title : ''}
                      tag="h2"
                      weight="lg"
                      size="xs"
                      responsiveSize={{
                        sm: 'sm',
                      }}
                      className={`ellipsis-2L mb-xxs fc-dark`}
                    >
                      {title}
                    </Text>
                  </div>
                )}
                <Stack x alignItems={'center'} justifyContent="flex-start">
                  <GuideByline
                    {...{
                      guideAvatarUrl,
                      guideByline,
                      navigateToGuideAction,
                      livenessType,
                    }}
                  />
                </Stack>
              </Stack>
            )}
          </Layout>
        }
        footer={
          <Layout>
            <Stack y>
              <Stack x={!wide} y={!!wide} alignItems="center" alignSelf="flex-end">
                <div>
                  {isLive ? (
                    <StatusBadge clearBg={!!wide} className={wide ? styles.live : 'flex-center'}>
                      <LivePulse />
                      <div>{showAltLiveLabels ? t('onNow') : t('live')}</div>
                      {typeof start === 'string' ? <div className={formatLiveTimeStyles}>{liveTimeStart}</div> : null}
                    </StatusBadge>
                  ) : null}
                  {wide ? (isLive ? null : dateDisplay) : isLive ? null : dateDisplay}
                </div>
                {!wide && primaryAction?.label ? (
                  <>
                    {isLobbyButton ? (
                      <CardPrimaryAction primaryAction={bookedPrimaryAction}>
                        <Button size="sm" theme="secondary" label={bookedPrimaryAction.label} />
                      </CardPrimaryAction>
                    ) : (
                      primaryActionButton
                    )}
                  </>
                ) : null}
              </Stack>
            </Stack>
          </Layout>
        }
        data-id={id}
      />
    </>
  )
}

export default Feed
